import React from "react";
import posthog from "posthog-js";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
} from "react-router";
import type { LinksFunction, LoaderFunction } from "react-router";

import "@repo/ui/globals.css";
import { FatalError } from "@repo/ui/components/fatal-error";
import { useIdentity } from "@/infrastructure/use-identity";

export const links: LinksFunction = () => [
  // {
  //   rel: "stylesheet",
  //   href: "https://fonts.googleapis.com/css?family=Parisienne&display=swap",
  // },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/apple-touch-icon.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "32x32",
    href: "/favicon-32x32.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "16x16",
    href: "/favicon-16x16.png",
  },
  {
    rel: "icon",
    type: "image/x-icon",
    href: "/favicon.ico",
  },
  { rel: "manifest", href: "/site.webmanifest" },
];

export const loader: LoaderFunction = async () => {
  return {
    env: {
      PUBLIC_POSTHOG_KEY: process.env.VITE_PUBLIC_POSTHOG_KEY,
      PUBLIC_POSTHOG_HOST: process.env.VITE_PUBLIC_POSTHOG_HOST,
    },
  };
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  return (
    <div className="flex h-dvh w-full items-center justify-center">
      <FatalError
        message={error instanceof Error ? error.message : "Unknown error"}
      />
    </div>
  );
};

export default function App() {
  const { env } = useLoaderData<typeof loader>();
  const matches = useMatches();
  const reversedMatches = [...matches].reverse();
  const matchWithLang = reversedMatches.find((match) => {
    return (
      match.handle &&
      typeof match.handle === "object" &&
      "language" in match.handle
    );
  });

  let lang = "en";

  if (matchWithLang) {
    const languageFn = (matchWithLang.handle as any).language;
    if (typeof languageFn === "function") {
      const langVal = languageFn({ data: matchWithLang.data });
      if (typeof langVal === "string") {
        lang = langVal;
      }
    }
  }

  const isEmbeddable = matches.some((match) => {
    return (
      match.handle &&
      typeof match.handle === "object" &&
      "embeddable" in match.handle
    );
  });

  // Update document attributes dynamically
  React.useEffect(() => {
    document.documentElement.lang = lang;
    document.body.className = isEmbeddable ? "embed-theme" : "";
  }, [lang, isEmbeddable]);

  return <Outlet context={{ env }} />;
}
